import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Container,
  Grid,
  Typography,
  Button,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Badge,
} from '@mui/material';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { format } from 'date-fns';
import { getConversation } from 'src/service/conversationApi';
import { getUnreadMessages } from 'src/utils/messageUtils';
import StatisticsPDF from 'src/components/StatisticsPDF';

// Composant pour les cartes de statistiques avec animation
const StatCard = ({ title, value, icon, color, subtitle }) => (
  <div className="p-6 h-full bg-white rounded-lg shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
    <div className="flex items-center space-x-4">
      <div className={`w-14 h-14 rounded flex items-center justify-center bg-${color}-100`}>
        <Icon icon={icon} width={32} height={32} className={`text-${color}-600`} />
      </div>
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <h3 className="text-2xl font-semibold mt-1 mb-0.5">{value}</h3>
        {subtitle && (
          <p className="text-xs text-gray-500">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// Composant pour les accès rapides
const QuickAccessCard = ({ title, description, icon, onClick, color, disabled, style, badgeCount }) => (
  <div 
    className={`p-6 bg-white rounded-lg shadow-sm transition-all duration-300 ${
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1 hover:shadow-lg'
    }`}
    onClick={disabled ? undefined : onClick}
    style={style}
  >
    <div className="space-y-4">
      <div className={`w-12 h-12 rounded flex items-center justify-center bg-${color}-100 relative`}>
        <Icon icon={icon} width={24} height={24} className={`text-${color}-600`} />
        {badgeCount && badgeCount > 0 && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {badgeCount > 9999 ? '9999+' : badgeCount}
          </div>
        )}
      </div>
      <div>
        <h6 className="text-lg font-semibold mb-2">{title}</h6>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  </div>
);

// Composant pour afficher les catégories avec graphique circulaire
const CategoryList = ({ categories }) => (
  <div className="p-6 bg-white rounded-lg shadow-sm h-full">
    <h6 className="text-lg font-semibold mb-4">Répartition par catégorie</h6>
    <div className="space-y-6">
      {categories.map((category) => {
        const found = parseInt(category.found_returned) + parseInt(category.found_sent) + 
                     parseInt(category.found_to_send) + parseInt(category.found_paid) + 
                     parseInt(category.found_found);
        const restituted = parseInt(category.found_returned);
        const restitutedPercentage = found > 0 ? Math.round((restituted / found) * 100) : 0;
        
        return (
          <div key={category.name}>
            <div className="flex justify-between items-center mb-1">
              <p className="text-base font-medium">{category.name}</p>
              <p className="text-sm text-gray-600">{found} objets trouvés</p>
            </div>
            <div className="flex space-x-4 items-center">
              <div className="flex-grow">
                <div className="flex justify-between mb-1">
                  <p className="text-xs text-green-600">Objets trouvés</p>
                  <p className="text-xs text-green-600">{found}</p>
                </div>
                <div className="relative h-2 mb-2">
                  <div 
                    className="absolute w-full h-2 bg-green-200 rounded-full"
                  />
                  <div 
                    className="absolute h-2 bg-green-500 rounded-full"
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="flex justify-between mb-1">
                  <p className="text-xs text-blue-600">Objets restitués ({restitutedPercentage}%)</p>
                  <p className="text-xs text-blue-600">{restituted}</p>
                </div>
                <div className="relative h-2">
                  <div 
                    className="absolute w-full h-2 bg-blue-200 rounded-full"
                  />
                  <div 
                    className="absolute h-2 bg-blue-500 rounded-full"
                    style={{ width: `${restitutedPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// Composant pour afficher les statistiques détaillées
const DetailedStats = ({ global }) => (
  <div className="p-6 bg-white rounded-lg shadow-sm h-full">
    <h6 className="text-lg font-semibold mb-4">Détail des statuts</h6>
    <div className="grid grid-cols-1 gap-4">
      <div>
        <p className="text-sm font-medium text-green-600 mb-2">Objets Trouvés</p>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between mb-1">
              <p className="text-xs">Restitués</p>
              <p className="text-xs">{global.found_returned}</p>
            </div>
            <div className="relative h-1.5">
              <div 
                className="absolute h-1.5 bg-green-500 rounded-full"
                style={{ width: `${(parseInt(global.found_returned) / parseInt(global.total_annonces)) * 100}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <p className="text-xs">En cours d'envoi</p>
              <p className="text-xs">{global.found_sent}</p>
            </div>
            <div className="relative h-1.5">
              <div 
                className="absolute h-1.5 bg-blue-500 rounded-full"
                style={{ width: `${(parseInt(global.found_sent) / parseInt(global.total_annonces)) * 100}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <p className="text-xs">À expédier</p>
              <p className="text-xs">{global.found_to_send}</p>
            </div>
            <div className="relative h-1.5">
              <div 
                className="absolute h-1.5 bg-yellow-500 rounded-full"
                style={{ width: `${(parseInt(global.found_to_send) / parseInt(global.total_annonces)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState(format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd\'T\'HH:mm'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'));

  const fetchUnreadMessages = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const customerId = localStorage.getItem('customerId');
      
      if (!token || !customerId) {
        return;
      }

      const conversations = await getConversation(`?annonce.customer=${customerId}`);
      
      // Filtrer les conversations (exclure RESTITUÉ et ENVOYÉ)
      const filteredConversations = conversations.filter((conv) => {
        if (conv.annonce.status === "RESTITUÉ" || conv.annonce.status === "ENVOYÉ") {
          return false;
        }
        return true;
      });

      // Compter les messages non lus
      let totalUnread = 0;
      filteredConversations.forEach((conversation) => {
        const unreadMessages = getUnreadMessages(conversation.messages || []);
        totalUnread += unreadMessages.length;
      });

      setUnreadMessagesCount(totalUnread);
    } catch (error) {
      console.error('Erreur lors de la récupération des messages non lus:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const customerId = localStorage.getItem('customerId');
      
      if (!token || !customerId) {
        console.error('Token ou CustomerId manquant');
        navigate('/login');
        return;
      }

      const apiUrl = `${process.env.REACT_APP_PUBLIC_API}/statistics/customer/${customerId}`;
      
      const requestParams = {
        startDate: period === 'all' ? undefined : startDate,
        endDate: period === 'all' ? undefined : endDate,
      };
      
      const response = await axios.get(
        apiUrl,
        {
          params: requestParams,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000, // Timeout de 10 secondes
        }
      );
      
      setStatistics(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur détaillée:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });

      if (err.response?.status === 401) {
    
        navigate('/login');
      } else if (err.code === 'ECONNABORTED') {
        setError('Le serveur met trop de temps à répondre. Veuillez réessayer.');
      } else if (!err.response) {
        setError('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet.');
      } else {
        setError(`Erreur lors du chargement des statistiques: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchUnreadMessages();
  }, [startDate, endDate, period]);

  // Mettre à jour les messages non lus toutes les 60 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadMessages();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setPeriod(newPeriod);
    
    const today = new Date();
    let newStartDate;
    let newEndDate;

    switch (newPeriod) {
      case 'all':
        newStartDate = null;
        newEndDate = null;
        break;
      case 'week':
        newStartDate = new Date(today.setDate(today.getDate() - 7));
        newEndDate = new Date();
        break;
      case 'month':
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date();
        break;
      case 'year':
        newStartDate = new Date(today.getFullYear(), 0, 1);
        newEndDate = new Date();
        break;
      default:
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date();
    }

    if (newStartDate && newEndDate) {
      setStartDate(format(newStartDate, 'yyyy-MM-dd\'T\'HH:mm'));
      setEndDate(format(newEndDate, 'yyyy-MM-dd\'T\'HH:mm'));
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const handlePublishAnnonce = () => {
    navigate('/dashboard/publish');
  };

  const handleViewMessages = () => {
    navigate('/dashboard/messages');
  };

  const handleViewMatchings = () => {
    navigate('/dashboard/matchings');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 space-y-4 md:space-y-0">
        <h1 className="text-2xl font-bold">Tableau de bord</h1>
        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          <select
            value={period}
            onChange={handlePeriodChange}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Toutes périodes</option>
            <option value="week">7 derniers jours</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          
          {period !== 'all' && (
            <>
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium text-gray-700">Date et heure de début</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium text-gray-700">Date et heure de fin</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}
          
          <StatisticsPDF 
            statistics={statistics}
            period={period}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total des annonces"
          value={statistics?.global?.total_annonces || 0}
          icon="mdi:file-document-outline"
          color="blue"
        />
        <StatCard
          title="Objets trouvés"
          value={
            parseInt(statistics?.global?.found_returned || 0) +
            parseInt(statistics?.global?.found_sent || 0) +
            parseInt(statistics?.global?.found_to_send || 0) +
            parseInt(statistics?.global?.found_paid || 0) +
            parseInt(statistics?.global?.found_found || 0)
          }
          icon="mdi:check-circle-outline"
          color="green"
        />
        <StatCard
          title="Objets restitués"
          value={
            parseInt(statistics?.global?.found_returned || 0) +
            parseInt(statistics?.global?.lost_returned || 0)
          }
          icon="mdi:hand-heart"
          color="green"
        />
        <StatCard
          title="Taux de restitution"
          value={`${
            statistics?.global?.total_annonces > 0
              ? Math.round(
                  ((parseInt(statistics?.global?.found_returned || 0) +
                    parseInt(statistics?.global?.lost_returned || 0)) /
                    parseInt(statistics?.global?.total_annonces)) *
                    100
                )
              : 0
          }%`}
          icon="mdi:percent"
          color="purple"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <CategoryList categories={statistics?.categories || []} />
        </div>
        <div>
          <DetailedStats global={statistics?.global || {}} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <QuickAccessCard
          title="Publier une annonce"
          description="Créer une nouvelle annonce pour un objet trouvé "
          icon="mdi:plus-circle-outline"
          onClick={handlePublishAnnonce}
          color="blue"
        />
        <QuickAccessCard
          title="Messages"
          description="Consulter vos messages et notifications"
          icon="mdi:message-outline"
          onClick={handleViewMessages}
          color="green"
          badgeCount={unreadMessagesCount}
        />
        <QuickAccessCard
          title="Matchings"
          description="Non disponible pour les festivals"
          icon="mdi:link-variant"
          onClick={handleViewMatchings}
          color="purple"
          disabled={true}
        />
      </div>
    </div>
  );
} 